import { Skeleton, Card, CardContent } from "@wondermaps/ui";
import { POIHeatmap } from "./sections/POIHeatmap";
import { SponsoredPOIs } from "./sections/SponsoredPOIs";
import { MapSponsorROI } from "./sections/MapSponsorROI";
import { SearchIntent } from "./sections/SearchIntent";
import { DeadZones } from "./sections/DeadZones";
import { EngagementFunnel } from "./sections/EngagementFunnel";
import { FeedbackSummary } from "./sections/FeedbackSummary";
import { BehaviorRetention } from "./sections/BehaviorRetention";
import { DeviceChannel } from "./sections/DeviceChannel";
import type { EngagementReportData } from "./types";

interface Props {
  data: EngagementReportData | undefined;
  isLoading: boolean;
  error: Error | null;
  deadZoneThreshold: number;
  venueContext?: {
    totalVenueVisitors?: number;
    operatingDays?: number;
  };
}

export function ReportDashboard({
  data,
  isLoading,
  error,
  deadZoneThreshold,
  venueContext,
}: Props) {
  if (isLoading) {
    return (
      <div className="space-y-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <Skeleton className="h-40 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-destructive">
            Failed to generate report: {error.message}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  // Venue context KPIs
  const mapUsers = data.deviceChannel.uniqueVisitors;
  const adoptionRate =
    venueContext?.totalVenueVisitors && venueContext.totalVenueVisitors > 0
      ? Math.round((mapUsers / venueContext.totalVenueVisitors) * 1000) / 10
      : null;
  const dailyAvg =
    venueContext?.operatingDays && venueContext.operatingDays > 0
      ? Math.round(mapUsers / venueContext.operatingDays)
      : null;

  return (
    <div className="space-y-6" id="engagement-report">
      {/* Venue context KPIs */}
      {(adoptionRate !== null || dailyAvg !== null) && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <KPIBox label="Map Users" value={mapUsers.toLocaleString()} />
          {venueContext?.totalVenueVisitors && (
            <KPIBox
              label="Total Venue Visitors"
              value={venueContext.totalVenueVisitors.toLocaleString()}
            />
          )}
          {adoptionRate !== null && (
            <KPIBox label="Adoption Rate" value={`${adoptionRate}%`} highlight />
          )}
          {dailyAvg !== null && (
            <KPIBox label="Daily Avg Map Users" value={dailyAvg.toLocaleString()} />
          )}
        </div>
      )}

      <POIHeatmap data={data.poiHeatmap} />
      <SponsoredPOIs data={data.sponsoredPois} />
      <MapSponsorROI data={data.mapSponsors} />
      <SearchIntent data={data.search} />
      <DeadZones data={data.deadZones} threshold={deadZoneThreshold} />
      <EngagementFunnel
        data={data.funnel}
        outboundClicksTotal={data.outboundClicksTotal}
      />
      <FeedbackSummary data={data.feedback} />
      <BehaviorRetention data={data.behavior} timezone={data.timezone} />
      <DeviceChannel data={data.deviceChannel} />
    </div>
  );
}

function KPIBox({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="rounded-lg border p-4 text-center">
      <p className={`text-3xl font-bold ${highlight ? "text-primary" : ""}`}>
        {value}
      </p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}
